import '../styles/globals.css'
import Script from 'next/script'
import { Inter } from 'next/font/google'
import { getSession, SessionProvider } from 'next-auth/react'
import { useEffect } from 'react'
import { useRouter } from 'next/router'

const inter = Inter({ subsets: ['latin'] })

const GA_MEASUREMENT_ID = 'G-L7P6W1D47W'
const CLARITY_PROJECT_ID = 'vx8hwtbdnj'
const CRISP_WEBSITE_ID = 'e11fc6e5-0079-41f9-8e80-a3ccc5c6234f'

const VISIT_FLAG_KEY = '10xp_visit_logged_v1';
const VISIT_SESSION_KEY = '10xp_visit_session_id_v1';
const ENGAGEMENT_FLAG_KEY = '10xp_engagement_logged_v1';

// Module-level guard against duplicate concurrent pings within the same
// page session. Persists across client-side navs (the _app module stays
// loaded) but resets on full page reload — which is fine, since the
// sessionStorage flag is also unset on a fresh load if the previous ping
// never succeeded.
let visitInFlight = false;

function getVisitSessionId() {
  let sessionId = window.sessionStorage.getItem(VISIT_SESSION_KEY);
  if (!sessionId) {
    sessionId =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      Math.random().toString(36).slice(2) + Date.now().toString(36);
    window.sessionStorage.setItem(VISIT_SESSION_KEY, sessionId);
  }
  return sessionId;
}

function getUtcDateKey() {
  return new Date().toISOString().slice(0, 10);
}

// Fire-and-forget ping to /api/visit once per browser session, so the admin
// dashboard can show first-party visitor counts. Skipped on admin/api routes
// to avoid polluting the numbers with internal traffic. The durable
// sessionStorage flag is set only after a 204 response — a 500 (DB insert
// failure) or network error leaves it unset so the next navigation retries.
function VisitLogger() {
  const router = useRouter();
  const { pathname } = router;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!pathname) return;
    if (pathname.startsWith('/admin') || pathname.startsWith('/api')) return;

    let alreadyLogged = false;
    try {
      alreadyLogged = window.sessionStorage.getItem(VISIT_FLAG_KEY) === '1';
    } catch {
      // sessionStorage can throw in private-mode / locked-down browsers —
      // skip the ping rather than crash the page.
      return;
    }
    if (alreadyLogged) return;
    if (visitInFlight) return;
    visitInFlight = true;

    let sessionId;
    try {
      sessionId = getVisitSessionId();
    } catch {
      visitInFlight = false;
      return;
    }

    const body = {
      path: pathname || '/',
      referrer: document.referrer || null,
      sessionId,
    };

    fetch('/api/visit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    })
      .then((res) => {
        if (res.status === 204) {
          // Success or intentional skip (bot/path) — mark durably logged
          // so we don't ping again this session.
          try {
            window.sessionStorage.setItem(VISIT_FLAG_KEY, '1');
          } catch {
            // ignore
          }
        } else {
          // Non-204 (e.g. 500 from a DB insert failure) — allow retry on
          // the next navigation.
          visitInFlight = false;
        }
      })
      .catch(() => {
        // Network failure — allow retry on the next navigation.
        visitInFlight = false;
      });
  }, [pathname]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!pathname) return;
    if (pathname.startsWith('/admin') || pathname.startsWith('/api')) return;

    const listeners = [
      ['pointerdown', 'pointer'],
      ['keydown', 'keyboard'],
      ['touchstart', 'touch'],
      ['wheel', 'wheel'],
    ];
    let engagementInFlight = false;
    let sessionChecked = false;
    let userId = null;
    let recordedActivityKey = null;
    let interactionListenersAttached = false;

    function addInteractionListeners() {
      if (interactionListenersAttached) return;
      interactionListenersAttached = true;
      for (const [eventName] of listeners) {
        window.addEventListener(eventName, handleEngagement, { passive: true });
      }
    }

    function removeInteractionListeners() {
      interactionListenersAttached = false;
      for (const [eventName] of listeners) {
        window.removeEventListener(eventName, handleEngagement);
      }
    }

    function refreshSessionOnReturn() {
      // Auth can change in another tab while this route remains mounted. A
      // focus/visibility transition happens before the next interaction, so
      // force that interaction to resolve the current account again.
      sessionChecked = false;
      userId = null;
      recordedActivityKey = null;
      addInteractionListeners();
    }

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') refreshSessionOnReturn();
    }

    async function handleEngagement(event) {
      if (!event.isTrusted || engagementInFlight) return;

      engagementInFlight = true;
      try {
        if (!sessionChecked) {
          const session = await getSession();
          userId = session?.user?.id || null;
          sessionChecked = true;
          if (!userId) {
            // Signed-out marketing-page interactions are not DAU. Stop after
            // one session check instead of sending a request on every event.
            removeInteractionListeners();
            return;
          }
        }

        // Include both account and UTC date. The date is evaluated on every
        // event so a planner tab left open across midnight re-arms itself.
        const activityKey = `${userId}:${getUtcDateKey()}`;
        if (recordedActivityKey === activityKey) return;
        try {
          if (window.sessionStorage.getItem(ENGAGEMENT_FLAG_KEY) === activityKey) {
            recordedActivityKey = activityKey;
            return;
          }
        } catch {
          // Local in-memory deduplication still works when storage is blocked.
        }

        const kind = listeners.find(([eventName]) => eventName === event.type)?.[1] || 'pointer';
        const res = await fetch('/api/visit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            event: 'engagement',
            engagementKind: kind,
            path: pathname || '/',
          }),
          keepalive: true,
        });

        if (res.status === 204) {
          recordedActivityKey = activityKey;
          try {
            window.sessionStorage.setItem(ENGAGEMENT_FLAG_KEY, activityKey);
          } catch {
            // ignore
          }
        } else if (res.status === 401) {
          // The session expired after the client check. Avoid retrying on
          // every interaction until focus, visibility, or navigation makes
          // the tracker resolve the account again.
          removeInteractionListeners();
        }
      } catch {
        // Keep the listeners attached so the next interaction retries.
      } finally {
        engagementInFlight = false;
      }
    }

    addInteractionListeners();
    window.addEventListener('focus', refreshSessionOnReturn);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      removeInteractionListeners();
      window.removeEventListener('focus', refreshSessionOnReturn);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pathname]);

  return null;
}

export default function App({ Component, pageProps }) {
  const page = <Component {...pageProps} />;

  return (
    <>
      <style jsx global>{`
        html {
          font-family: ${inter.style.fontFamily};
        }
      `}</style>

      {/* Google Analytics */}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_MEASUREMENT_ID}');
        `}
      </Script>

      {/* Microsoft Clarity */}
      <Script id="microsoft-clarity" strategy="afterInteractive">
        {`
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
        `}
      </Script>

      {/* Crisp customer support chat */}
      <Script id="crisp-chat" strategy="afterInteractive">
        {`
          window.$crisp = window.$crisp || [];
          window.CRISP_WEBSITE_ID = '${CRISP_WEBSITE_ID}';

          (function() {
            var script = document.createElement('script');
            script.src = 'https://client.crisp.chat/l.js';
            script.async = true;
            document.head.appendChild(script);
          })();
        `}
      </Script>

      <VisitLogger />
      {Component.skipSessionProvider ? (
        page
      ) : (
        <SessionProvider session={pageProps.session}>{page}</SessionProvider>
      )}
    </>
  )
}
